import React, { useState, useRef } from 'react';

import { Box, useTheme, useMediaQuery } from '@mui/material';

import { useLocation, useHistory } from 'react-router';

import { useFormik } from 'formik';

import BillingData from '@shared/components/BillingData';
import PaymentMethodSelector from '@shared/components/PaymentMethodSelector';
import { BookingSummaryText } from './BookingSummaryText';

import { flightSales } from '@shared/services/services';

import { validateCardInfo } from './helpers';

import { FLIGHTS_RESERVE_SUCCESSFUL } from '@shared/helpers/routes';
import { useBillingError } from '@shared/hooks/useBillingError';
import billingBookingSchema from '@shared/components/BillingData/billingBookingSchema';

const PaymentStarlings = ({ flightValidate = {}, cardData, setCardData }) => {
  const location = useLocation();
  const history = useHistory();

  const { PNR, formReserve, flightsInUSD, id_user } = location.state ?? {};

  const { contactInfo, selectedSubsidiary } = formReserve || {};

  const flightCluster = flightValidate?.cluster;
  const isDolar = !!flightCluster?.composition_price?.tax_detail?.QO;

  const [infoCard, setInfoCard] = useState(null);

  const [isLoadingPay, setIsLoadingPay] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formikPaymentRef = useRef(null);
  const paymentSelectorRef = useRef(null);

  const {
    billingError,
    showBillingError,
    clearBillingError,
  } = useBillingError();

  const handleChangeInfoCard = data => {
    setInfoCard(data);
  };

  const handleFlightSalesError = (error, PNR) => {
    const status = error.response?.status;
    const data = error.response?.data;
    const details = data?.details;
    const emissionResult = details?.emission_result;

    const is400 = status === 400;
    const isPartialEmission409 =
      status === 409 && emissionResult?.partialEmission === true;

    if (!is400 && !isPartialEmission409) {
      return false;
    }

    if (details?.payment_result) {
      history.push({
        pathname: FLIGHTS_RESERVE_SUCCESSFUL,
        state: {
          emission: {
            emission_result: emissionResult,
          },
          ...location.state,
          cardData,
        },
      });

      return true;
    }

    let finalMessage = data?.message || 'Ocurrió un error';
    let finalSubText = '';

    let finalTitle = 'Ocurrió un error al procesar la compra';

    if (finalMessage === '[object Object]') {
      finalMessage =
        'Por favor, revise los datos de su tarjeta e intente nuevamente';
      finalSubText =
        'Asegurate de que el número, la fecha de vencimiento y el CVV sean correctos.';
    }

    if (PNR) {
      finalTitle = 'No pudimos completar tu compra';
      finalMessage = BookingSummaryText(PNR);

      finalSubText =
        'Tené a mano tu código de reserva para agilizar la atención.';
    }

    showBillingError({
      title: finalTitle,
      message: finalMessage,
      subText: finalSubText,
    });

    setIsLoadingPay(false);
    return true;
  };

  const formik = useFormik({
    initialValues: {
      ownerCardName: '',
      ownerCardLastName: '',
      ownerDocument: '',
      afip: '',
      street: '',
      streetNumber: '',
      floor: '',
      province: '',
      department: '',
      postalCode: '',
      city: '',
    },
    validationSchema: billingBookingSchema({
      isMobile,
      withEntity: false,
    }),
    onSubmit: async values => {
      if (Object.keys(formik.errors).length > 0) {
        return;
      }

      if (infoCard == undefined || validateCardInfo(infoCard)) {
        showBillingError({
          title: 'Hubo un error en la carga de datos de la tarjeta',
          message:
            'Por favor, verifique la información ingresada e intente nuevamente.',
          subText: '',
        });
        return;
      }

      setIsLoadingPay(true);

      const bodyPay = {
        id_transaction: PNR.id_transaction,
        payment_plan_id: infoCard.idPlan ? infoCard.idPlan : null,
        client_email: contactInfo?.email,
        agency_email: selectedSubsidiary?.email,
        payment_object: {
          card_number: infoCard.cardInfo.number,
          card_exp_month: infoCard.cardInfo.card_exp_month,
          card_exp_year: infoCard.cardInfo.card_exp_year,
          card_code_value: infoCard.cardInfo.cvc,
          currency: 'ARS',
          card_holder_name: infoCard.cardInfo.name,
          card_holder_identification_type: 'dni',
          card_holder_identification_number: infoCard.cardInfo.dni,
          file_number: '-',
          ref_passenger: '-',
          agency_name: selectedSubsidiary?.name,
          agency_business_name: selectedSubsidiary?.business_name
            ? selectedSubsidiary?.business_name
            : 'string',
          user: String(id_user),
        },

        emission_object: {
          type_of_emission: 0,
          PNR_code: PNR.PNR_code,
          id_integration: 2,
          billing_data: {
            owner_card_name: values.ownerCardName,
            owner_card_last_name: values.ownerCardLastName,
            owner_document: values.ownerDocument,
            afip: values.afip,
            street: values.street,
            street_number: values.streetNumber,
            floor: values.floor ? values.floor : '',
            department: values.department ? values.department : '',
            city: values.city,
            postal_code: values.postalCode,
            province: values.province,
          },
        },
      };

      flightSales(bodyPay)
        .then(res => {
          if (!res) return;

          history.push({
            pathname: FLIGHTS_RESERVE_SUCCESSFUL,
            state: { emission: res, ...location.state, cardData },
          });
        })
        .catch(error => {
          if (!handleFlightSalesError(error, PNR)) {
            console.error('Error no manejado:', error);
          }
        });
    },
  });

  return (
    <Box>
      <PaymentMethodSelector
        ref={paymentSelectorRef}
        handleChangeInfoCard={handleChangeInfoCard}
        precio={flightValidate?.cluster?.composition_price}
        setCardData={setCardData}
        cardData={cardData}
        idPlan={isDolar ? 6 : 5}
        isInUSD={flightsInUSD?.isInUSD}
        formikPaymentRef={formikPaymentRef}
      />
      <BillingData
        formik={formik}
        billingError={billingError}
        clearBillingError={clearBillingError}
        formikPaymentRef={formikPaymentRef}
        paymentSelectorRef={paymentSelectorRef}
        showConfirmButton={true}
        isLoadingPay={isLoadingPay}
        isMobile={isMobile}
      />
    </Box>
  );
};

export default PaymentStarlings;
